import React from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EmptyState } from '@/components/custom/empty-state';

echarts.use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const COLORS = { total: '#5B3FD3', flagged: '#E5484D', bar: '#5B3FD3' };
const TOOLTIP = { backgroundColor: 'rgba(255, 255, 255, 0.9)', borderWidth: 0, shadowColor: 'rgba(0, 0, 0, 0.1)', shadowBlur: 10, textStyle: { color: '#666' } };
const AXIS_LABEL = { color: '#999', fontSize: 11 };

function Chart({ option, height = 280 }) {
  return (
    <ReactECharts
      option={option}
      echarts={echarts}
      style={{ height, width: '100%' }}
      notMerge
    />
  );
}

const Empty = () => (
  <EmptyState
    type="data"
    variant="plain"
    text="Nenhum dado disponível"
  />
);

// Mensagens por dia, com as sinalizadas em destaque.
export function DailyChart({ data = [] }) {
  if (!data.length) return <Empty />;
  const series = [
    { name: 'Mensagens', key: 'total', color: COLORS.total },
    { name: 'Sinalizadas', key: 'flagged', color: COLORS.flagged }
  ];
  return (
    <Chart
      option={{
        grid: { left: 15, right: '3%', bottom: '15%', top: '8%', containLabel: true },
        tooltip: { trigger: 'axis', ...TOOLTIP },
        legend: { bottom: 0, icon: 'roundRect', itemWidth: 20, itemHeight: 2, textStyle: { color: '#8894AC', fontSize: 12 } },
        xAxis: { type: 'category', data: data.map(row => row.date.slice(5).split('-').reverse().join('/')), boundaryGap: false, axisLine: { show: false }, axisTick: { show: false }, axisLabel: AXIS_LABEL },
        yAxis: { type: 'value', min: 0, minInterval: 1, axisLabel: AXIS_LABEL, splitLine: { lineStyle: { color: '#ECE9F8' } } },
        series: series.map(({ name, key, color }) => ({ name, type: 'line', data: data.map(row => row[key]), lineStyle: { width: 2, color }, itemStyle: { color }, symbol: 'circle', symbolSize: 6 }))
      }}
    />
  );
}

// Mensagens por hora do dia (0h–23h).
export function HourlyChart({ data = [] }) {
  if (!data.some(row => row.total > 0)) return <Empty />;
  return (
    <Chart
      option={{
        grid: { left: 15, right: '3%', bottom: '8%', top: '8%', containLabel: true },
        tooltip: { trigger: 'axis', ...TOOLTIP },
        xAxis: { type: 'category', data: data.map(row => `${row.hour}h`), axisLine: { show: false }, axisTick: { show: false }, axisLabel: AXIS_LABEL },
        yAxis: { type: 'value', min: 0, minInterval: 1, axisLabel: AXIS_LABEL, splitLine: { lineStyle: { color: '#ECE9F8' } } },
        series: [{ type: 'bar', data: data.map(row => row.total), itemStyle: { color: COLORS.bar, borderRadius: [4, 4, 0, 0] }, barMaxWidth: 18 }]
      }}
    />
  );
}

// Barras horizontais ordenadas (desfecho, seção, intenção, escola...). `rows`: [{ key, total }].
export function RankingChart({ rows = [], labelFor = key => key }) {
  if (!rows.length) return <Empty />;
  const ordered = [...rows].reverse();
  return (
    <Chart
      height={Math.max(220, rows.length * 34 + 40)}
      option={{
        grid: { left: 10, right: '8%', bottom: 10, top: 10, containLabel: true },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, ...TOOLTIP },
        xAxis: { type: 'value', minInterval: 1, axisLabel: AXIS_LABEL, splitLine: { lineStyle: { color: '#ECE9F8' } } },
        yAxis: { type: 'category', data: ordered.map(row => labelFor(row.key)), axisLine: { show: false }, axisTick: { show: false }, axisLabel: { ...AXIS_LABEL, width: 160, overflow: 'truncate' } },
        series: [{ type: 'bar', data: ordered.map(row => row.total), itemStyle: { color: COLORS.bar, borderRadius: [0, 4, 4, 0] }, barMaxWidth: 20, label: { show: true, position: 'right', color: '#666', fontSize: 11 } }]
      }}
    />
  );
}
